import React from "react";
import "./src/styles/global.css";
// Import Prism.js styles for syntax highlighting
import "prismjs/themes/prism.css";
import "prismjs/plugins/line-numbers/prism-line-numbers.css";
// Import KaTeX CSS for math rendering
import "katex/dist/katex.min.css";
import { ThemeProvider } from "./src/context/ThemeContext";

const headingCopyTimeouts = new WeakMap();
const copyFlashMs = 1400;
const headingTopGap = 12;

const copyTextToClipboard = async (text) => {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  return false;
};

const getStickyHeaderOffset = () => {
  if (globalThis.window === undefined || globalThis.document === undefined) {
    return 0;
  }

  const header = document.querySelector("header");
  if (!header) {
    return 0;
  }

  const style = globalThis.window.getComputedStyle(header);
  if (style.position !== "sticky" && style.position !== "fixed") {
    return 0;
  }

  return header.getBoundingClientRect().height + headingTopGap;
};

const scrollToHash = (hash) => {
  if (
    globalThis.window === undefined ||
    globalThis.document === undefined ||
    !hash
  ) {
    return;
  }

  const rawId = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!rawId) {
    return;
  }

  const id = decodeURIComponent(rawId);
  const target = document.getElementById(id);
  if (!target) {
    return;
  }

  const top =
    target.getBoundingClientRect().top +
    globalThis.window.scrollY -
    getStickyHeaderOffset();
  globalThis.window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
};

const markHeadingCopied = (anchor) => {
  anchor.dataset.copied = "true";
  const existingTimeout = headingCopyTimeouts.get(anchor);
  if (existingTimeout) {
    globalThis.clearTimeout(existingTimeout);
  }

  const timeoutId = globalThis.setTimeout(() => {
    anchor.dataset.copied = "false";
  }, copyFlashMs);

  headingCopyTimeouts.set(anchor, timeoutId);
};

const setupHeadingAnchors = () => {
  if (globalThis.document === undefined) {
    return;
  }

  const anchors = document.querySelectorAll(".heading-anchor");
  anchors.forEach((anchor) => {
    if (anchor.dataset.copyLinkBound === "true") {
      return;
    }

    anchor.dataset.copyLinkBound = "true";
    anchor.setAttribute("aria-label", "Copy link to section");
    anchor.setAttribute("title", "Copy link to section");

    anchor.addEventListener("click", (event) => {
      const href = anchor.getAttribute("href");
      if (!href?.startsWith("#")) {
        return;
      }

      event.preventDefault();

      const targetUrl = `${globalThis.window.location.origin}${globalThis.window.location.pathname}${globalThis.window.location.search}${href}`;

      copyTextToClipboard(targetUrl).then((didCopy) => {
        if (didCopy) {
          markHeadingCopied(anchor);
        }
      });

      globalThis.window.history.replaceState(null, "", href);
      scrollToHash(href);
    });
  });
};

const scheduleHeadingSetup = () => {
  if (globalThis.window === undefined) {
    return;
  }

  globalThis.window.requestAnimationFrame(() => {
    setupHeadingAnchors();
    if (globalThis.window.location.hash) {
      scrollToHash(globalThis.window.location.hash);
    }
  });
};

export const wrapRootElement = ({ element }) => (
  <ThemeProvider>{element}</ThemeProvider>
);

// Prevent transition flash on page load
export const onInitialClientRender = () => {
  // Add preload class to prevent transitions during initial render
  document.documentElement.classList.add("preload");
  document.body.classList.add("preload");

  // Remove preload class after theme is properly initialized
  const removePreload = () => {
    document.documentElement.classList.remove("preload");
    document.body.classList.remove("preload");
  };

  // Wait for next frame to ensure theme is applied
  globalThis.requestAnimationFrame(() => {
    globalThis.requestAnimationFrame(removePreload);
  });

  scheduleHeadingSetup();
};

export const onRouteUpdate = () => {
  scheduleHeadingSetup();
};
